import {
  AdminInput,
  Disclaimer,
  Div,
  DropdownMenuComponent,
  H5,
  Icon,
  ListSpinner,
  P,
  Radio,
  SimpleBrandWithImageEntity,
  X,
  lightTheme,
  useOnClickOutside,
} from '@vgrubs/components';
import {
  AdminSelectWrapperChildStyled,
  AdminSelectWrapperStyled,
  ChevronStyled,
  SelectedStyled,
} from 'components/src/adminComponents/AdminCustomSelect/AdminCustomSelect.styled';
import { SimpleLocationEntity } from 'components/src/types/location.types';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useImportPickerLocations } from './useImportPickerLocations';

const ChevronSvg = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.29365 12.7066C7.68428 13.0973 8.31865 13.0973 8.70928 12.7066L14.7093 6.70664C15.0999 6.31602 15.0999 5.68164 14.7093 5.29102C14.3187 4.90039 13.6843 4.90039 13.2937 5.29102L7.9999 10.5848L2.70615 5.29414C2.31553 4.90352 1.68115 4.90352 1.29053 5.29414C0.899902 5.68477 0.899902 6.31914 1.29053 6.70977L7.29053 12.7098L7.29365 12.7066Z"
      fill="#4C4C4C"
    />
  </svg>
);

const OptionRow = styled.div<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  margin-bottom: 8px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.gray};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  text-align: left;

  ${({ $selected, theme }) => $selected && `background-color: ${theme.gray};`}

  &:hover {
    ${({ $disabled, theme }) =>
      !$disabled && `background-color: ${theme.gray};`}
  }
`;

const ListWrapper = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding: 0 4px 4px 4px;
`;

const TrailingIcons = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translate(0, -50%);
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: none;
`;

const ClearIconWrapper = styled.div`
  pointer-events: auto;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 2px;
`;

export const ImportLocationPicker = ({
  selectedLocation,
  onSelect,
  disabledLocationId,
  placeholder = 'Select Location',
  clearable = false,
}: {
  selectedLocation: SimpleLocationEntity | null;
  onSelect: (location: SimpleLocationEntity | null) => void;
  disabledLocationId?: number | string | null;
  placeholder?: string;
  clearable?: boolean;
}) => {
  const [dropdown, setDropdown] = useState(false);

  const dropdownRef = useRef(null);
  useOnClickOutside(dropdownRef, () => dropdown && setDropdown(false));

  const {
    locations,
    inputValue,
    handleInputChange,
    observerRef,
    isFetchingNextPage,
    hasNextPage,
    resetSearch,
    query,
    isLoading,
  } = useImportPickerLocations({ enabled: dropdown });

  useEffect(() => {
    if (!dropdown && query) resetSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdown]);

  const handleSelect = (
    e: React.MouseEvent,
    location: SimpleLocationEntity
  ) => {
    e.stopPropagation();
    if (location.id === disabledLocationId) return;
    onSelect(location);
    setDropdown(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  const showClear = clearable && !!selectedLocation;

  return (
    <div>
      <AdminSelectWrapperStyled
        $opened={dropdown}
        ref={dropdownRef}
        onClick={() => setDropdown(true)}
      >
        <AdminSelectWrapperChildStyled>
          <Div $flex $alignCenter $width="100%">
            <SelectedStyled>
              {selectedLocation ? selectedLocation.name : placeholder}
            </SelectedStyled>
          </Div>
          {showClear ? (
            <TrailingIcons>
              <ClearIconWrapper onClick={handleClear}>
                <Icon $size="12">
                  <X fill={lightTheme.darkGray} />
                </Icon>
              </ClearIconWrapper>
            </TrailingIcons>
          ) : (
            <ChevronStyled>
              <ChevronSvg />
            </ChevronStyled>
          )}
        </AdminSelectWrapperChildStyled>

        <DropdownMenuComponent
          isOpen={dropdown}
          maxHeight="calc(50vh - 100px)"
          fullWidth
        >
          <Div
            $p="8px 8px 16px 8px"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <AdminInput
              search
              value={inputValue}
              onChange={handleInputChange}
              placeholder="Search Location"
            />
          </Div>
          <ListWrapper>
            {locations.length === 0 && !isFetchingNextPage && !isLoading && (
              <Div $flex $center $column $p="8px 16px">
                <P $m="0" $center>
                  No matches for "{query}".
                </P>
              </Div>
            )}
            {locations.map((l) => {
              const isSelected = selectedLocation?.id === l.id;
              const isDisabled = l.id === disabledLocationId;
              return (
                <OptionRow
                  key={l.id}
                  $selected={isSelected}
                  $disabled={isDisabled}
                  onClick={(e: React.MouseEvent) => handleSelect(e, l)}
                >
                  <div>
                    <P
                      $bold
                      $mb="4"
                      $color={
                        isSelected ? lightTheme.primary : lightTheme.darkGray
                      }
                      $lineHeight="1.2"
                    >
                      {l.name}
                    </P>
                    <Disclaimer $mb="0">{l.address}</Disclaimer>
                  </div>
                  <Radio checked={isSelected} />
                </OptionRow>
              );
            })}
            {(hasNextPage || isLoading) && (
              <ListSpinner
                loading
                observerRef={observerRef}
                smallest
                height="36px"
                heightSM="36px"
                mb="0"
              />
            )}
          </ListWrapper>
        </DropdownMenuComponent>
      </AdminSelectWrapperStyled>
    </div>
  );
};

export const ImportBrandPicker = ({
  brands,
  selectedBrand,
  onSelect,
  disabledBrandId,
  placeholder = 'Select Brand',
  disabled = false,
  clearable = false,
}: {
  brands: SimpleBrandWithImageEntity[];
  selectedBrand: SimpleBrandWithImageEntity | null;
  onSelect: (brand: SimpleBrandWithImageEntity | null) => void;
  disabledBrandId?: number | string | null;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
}) => {
  const [dropdown, setDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  useOnClickOutside(dropdownRef, () => dropdown && setDropdown(false));

  useEffect(() => {
    if (!dropdown && search) setSearch('');
  }, [dropdown, search]);

  const handleSelect = (
    e: React.MouseEvent,
    brand: SimpleBrandWithImageEntity
  ) => {
    e.stopPropagation();
    if (brand.id === disabledBrandId) return;
    onSelect(brand);
    setDropdown(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  const filteredBrands = brands.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  const showClear = clearable && !!selectedBrand && !disabled;

  return (
    <div>
      <AdminSelectWrapperStyled
        $opened={dropdown}
        ref={dropdownRef}
        onClick={() => !disabled && setDropdown(true)}
        style={disabled ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
      >
        <AdminSelectWrapperChildStyled>
          <Div $flex $alignCenter $width="100%">
            <SelectedStyled>
              {selectedBrand ? selectedBrand.name : placeholder}
            </SelectedStyled>
          </Div>
          {showClear ? (
            <TrailingIcons>
              <ClearIconWrapper onClick={handleClear}>
                <Icon $size="12">
                  <X fill={lightTheme.darkGray} />
                </Icon>
              </ClearIconWrapper>
            </TrailingIcons>
          ) : (
            <ChevronStyled>
              <ChevronSvg />
            </ChevronStyled>
          )}
        </AdminSelectWrapperChildStyled>

        <DropdownMenuComponent
          isOpen={dropdown}
          maxHeight="calc(50vh - 100px)"
          fullWidth
        >
          <Div
            $p="8px 8px 16px 8px"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <AdminInput
              search
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              placeholder="Search Brand"
            />
          </Div>
          <ListWrapper>
            {filteredBrands.length === 0 && (
              <Div $flex $center $column $p="24px 0">
                <H5 $m="0">No Brands</H5>
              </Div>
            )}
            {filteredBrands.map((b) => {
              const isSelected = selectedBrand?.id === b.id;
              const isDisabled = b.id === disabledBrandId;
              return (
                <OptionRow
                  key={b.id}
                  $selected={isSelected}
                  $disabled={isDisabled}
                  onClick={(e: React.MouseEvent) => handleSelect(e, b)}
                >
                  <P
                    $bold
                    $m="0"
                    $color={
                      isSelected ? lightTheme.primary : lightTheme.darkGray
                    }
                    $lineHeight="1.2"
                  >
                    {b.name}
                  </P>
                  <Radio checked={isSelected} />
                </OptionRow>
              );
            })}
          </ListWrapper>
        </DropdownMenuComponent>
      </AdminSelectWrapperStyled>
    </div>
  );
};
